import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Car, BedDouble, Pencil, Trash2, Star, Plus, Search, Image as ImageIcon, Archive, ListFilter } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, resolveMediaUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const STATUS_TONES = {
  available: "bg-emerald-50 text-emerald-700",
  negotiation: "bg-amber-50 text-amber-700",
  pending: "bg-blue-50 text-blue-700",
  sold: "bg-red-100 text-red-800",
  rented: "bg-violet-100 text-violet-800",
  inactive: "bg-red-50 text-red-700",
};

const STATUS_LABELS = {
  available: "Disponible",
  negotiation: "En négociation",
  pending: "Réservé",
  sold: "Vendu",
  rented: "Loué",
  inactive: "Désactivé",
};

export default function AdminListings() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams] = useSearchParams();
  const [sector, setSector] = useState(searchParams.get("sector") || "all");
  const [purpose, setPurpose] = useState("all");
  const [status, setStatus] = useState("all");
  const [view, setView] = useState("active");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError("");
    api
      .get("/listings", { params: { status: "", limit: 500 } })
      .then((r) => setItems(r.data))
      .catch((e) => {
        setLoadError(formatApiError(e.response?.data?.detail) || "Impossible de charger les annonces.");
        toast.error("Impossible de charger les annonces");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // If we just came from a wizard submit, refetch once shortly after mount
    // to avoid the create → nav race (backend visibility, cache, etc.)
    const t = setTimeout(load, 700);
    return () => clearTimeout(t);
  }, []);

  const filtered = useMemo(() => {
    return items.filter((l) => {
      const isHistory = ["sold", "rented", "inactive"].includes(l.status);
      if (view === "history" ? !isHistory : isHistory) return false;
      if (sector !== "all" && l.sector !== sector) return false;
      if (purpose !== "all" && l.purpose !== purpose) return false;
      if (status !== "all" && l.status !== status) return false;
      if (q) {
        const s = q.toLowerCase();
        if (
          !l.title?.toLowerCase().includes(s) &&
          !l.city?.toLowerCase().includes(s) &&
          !l.zone?.toLowerCase().includes(s) &&
          !l.category?.toLowerCase().includes(s)
        )
          return false;
      }
      return true;
    });
  }, [items, q, sector, purpose, status, view]);

  const counts = useMemo(() => {
    const c = { total: items.length, immo: 0, auto: 0, stays: 0, business: 0, available: 0, history: 0 };
    items.forEach((l) => {
      if (l.sector === "real_estate") c.immo += 1;
      if (l.sector === "auto") c.auto += 1;
      if (l.sector === "hospitality") c.stays += 1;
      if (l.sector === "business") c.business += 1;
      if (l.status === "available") c.available += 1;
      if (["sold", "rented", "inactive"].includes(l.status)) c.history += 1;
    });
    return c;
  }, [items]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Annonce supprimée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const quickStatus = async (l, newStatus) => {
    try {
      await api.patch(`/listings/${l.id}/status`, { status: newStatus });
      toast.success("Statut mis à jour");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const editHref = (l) => {
    const sectorKey = String(l.sector || "").toLowerCase();
    const isAuto = ["auto", "automobile", "vehicle", "vehicule", "véhicule"].includes(sectorKey);
    if (sectorKey === "hospitality") return `/admin/listings/stay/${l.id}`;
    if (sectorKey === "business") return `/admin/listings/business/${l.id}`;
    return isAuto ? `/admin/listings/auto/${l.id}` : `/admin/listings/immo/${l.id}`;
  };
  const cover = (l) => (l.images && l.images[0] ? resolveMediaUrl(l.images[0]) : null);

  return (
    <div className="space-y-6" data-testid="admin-listings">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Inventaire</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl text-brand-navy tracking-tight">{t("admin.listings")}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {counts.total} annonce(s) — {counts.immo} immobilier · {counts.auto} automobile · {counts.stays} séjour · {counts.business} business · {counts.available} disponible(s)
          </p>
        </div>
        <div className="w-full sm:w-auto">
          <Button asChild className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white sm:w-auto" data-testid="new-listing-wizard-btn">
            <Link to="/admin/listings/new"><Plus className="h-4 w-4 mr-1.5" /> Ajouter une annonce</Link>
          </Button>
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap gap-2" data-testid="listing-view-tabs">
          <Button type="button" variant={view === "active" ? "default" : "outline"} onClick={() => setView("active")} className={view === "active" ? "bg-brand-navy text-white" : ""}><ListFilter className="h-4 w-4" /> Annonces en cours</Button>
          <Button type="button" variant={view === "history" ? "default" : "outline"} onClick={() => setView("history")} className={view === "history" ? "bg-brand-navy text-white" : ""} data-testid="listing-history-tab"><Archive className="h-4 w-4" /> Historique ({counts.history})</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par titre, ville, quartier, catégorie…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="listings-search"
            />
          </div>
          <Select value={sector} onValueChange={setSector}>
            <SelectTrigger data-testid="filter-sector"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous secteurs</SelectItem>
              <SelectItem value="real_estate">Immobilier</SelectItem>
              <SelectItem value="auto">Automobile</SelectItem>
              <SelectItem value="hospitality">Hôtels & séjours</SelectItem>
              <SelectItem value="business">Business & Projects</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger data-testid="filter-purpose"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Vente + Location</SelectItem>
                <SelectItem value="sale">Vente</SelectItem>
                <SelectItem value="rent">Location</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid gap-3 md:hidden" data-testid="listings-mobile-cards">
        {!loading && filtered.length === 0 && <Card className="p-8 text-center text-slate-500">Aucune annonce ne correspond aux filtres.</Card>}
        {filtered.map((l) => <Card key={l.id} className="overflow-hidden p-3">
          <div className="flex gap-3">
            {cover(l) ? <img src={cover(l)} alt="" className="h-20 w-24 shrink-0 rounded-lg object-cover"/> : <div className="flex h-20 w-24 shrink-0 items-center justify-center rounded-lg bg-slate-100"><ImageIcon className="h-5 w-5 text-slate-400"/></div>}
            <div className="min-w-0 flex-1"><button type="button" onClick={() => nav(editHref(l))} className="line-clamp-2 text-left font-bold text-brand-navy">{l.title}</button><div className="mt-1 text-xs text-slate-500">{[l.zone, l.city].filter(Boolean).join(" · ")}</div><div className="mt-2 font-bold text-brand-orange">{Number(l.price || 0).toLocaleString()} {l.currency}</div></div>
          </div>
          <div className="mt-3 grid grid-cols-[1fr_auto_auto] gap-2 border-t pt-3"><Select value={l.status} onValueChange={(v) => quickStatus(l, v)}><SelectTrigger className={`h-11 text-xs ${STATUS_TONES[l.status] || "bg-slate-100"}`}><SelectValue/></SelectTrigger><SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select><Button variant="outline" size="icon" className="h-11 w-11" onClick={() => nav(editHref(l))} aria-label={`Modifier ${l.title}`}><Pencil className="h-4 w-4"/></Button><Button variant="outline" size="icon" className="h-11 w-11" onClick={() => remove(l.id)} aria-label={`Supprimer ${l.title}`}><Trash2 className="h-4 w-4 text-red-500"/></Button></div>
        </Card>)}
      </div>

      <Card className="hidden md:block">
        {loadError && <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{loadError}</div>}
        <Table data-testid="listings-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Média</TableHead>
              <TableHead>Titre</TableHead>
              <TableHead>Secteur</TableHead>
              <TableHead>Cat.</TableHead>
              <TableHead>Objet</TableHead>
              <TableHead>Prix</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>★</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">Chargement…</TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10">Aucune annonce ne correspond aux filtres.</TableCell></TableRow>
            )}
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`listing-row-${l.id}`}>
                <TableCell>
                  {cover(l) ? (
                    <img src={cover(l)} alt="" className="h-12 w-16 rounded object-cover border border-slate-200" />
                  ) : (
                    <div className="h-12 w-16 rounded border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => nav(editHref(l))}
                    className="text-sm font-semibold text-brand-navy hover:text-brand-orange text-left truncate max-w-xs"
                  >
                    {l.title}
                  </button>
                  <div className="text-xs text-slate-500">{[l.zone, l.city].filter(Boolean).join(" · ")}</div>
                  {l.public_until && ["sold", "rented"].includes(l.status) && <div className="mt-1 text-[11px] font-semibold text-slate-500">Retrait automatique du site : {new Date(l.public_until).toLocaleDateString("fr-FR")}</div>}
                </TableCell>
                <TableCell>
                  <span className="text-xs uppercase tracking-widest">{l.sector === "real_estate" ? "Immo" : l.sector === "hospitality" ? "Séjour" : l.sector === "business" ? "Business" : "Auto"}</span>
                </TableCell>
                <TableCell><span className="text-xs">{l.category}</span></TableCell>
                <TableCell>
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                    {l.purpose === "sale" ? "Vente" : "Location"}
                  </span>
                </TableCell>
                <TableCell className="font-bold">
                  {Number(l.price || 0).toLocaleString()} {l.currency}
                  {l.purpose === "rent" && l.price_period ? <span className="text-xs text-slate-500 font-normal">/{l.price_period}</span> : null}
                </TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => quickStatus(l, v)}>
                    <SelectTrigger className={`h-7 text-xs px-2 border border-slate-200 ${STATUS_TONES[l.status] || "bg-slate-100"}`} data-testid={`row-status-${l.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>{l.featured && <Star className="h-4 w-4 text-brand-orange fill-brand-orange" />}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" title="Modifier cette annonce" aria-label={`Modifier ${l.title}`} onClick={() => nav(editHref(l))} data-testid={`edit-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(l.id)} data-testid={`delete-${l.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
